import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Drawer, Heading, Select, Tag, TextField } from "@foris/avocado-suite";
import { useGetProgrammingStructuresStages } from "@services/collections/programming-structures/programmingStructuresServices";
import {
  getStagesAsSelectorOption,
  getStagesAsSelectorOptions,
} from "@services/collections/programming-structures/programmingStructuresAdapters";
import { AdaptedStageOption } from "@services/types/programming-structures/programmingStructuresTypes";
import { ProgrammingStructurePart, ProgrammingStructureStage } from "@models/ISchema";
import styles from "./stagesDrawer.module.scss";

interface StagesDrawerProps {
  isOpen?: boolean;
  scenario: string;
  selectedStructurePart?: ProgrammingStructurePart;
  savedStructureParts?: ProgrammingStructurePart[];
  onSaveStage?: (stage: ProgrammingStructurePart) => void;
  onClose?: () => void;
}

const StagesDrawer = ({
  isOpen,
  scenario,
  selectedStructurePart,
  savedStructureParts = [],
  onSaveStage,
  onClose,
}: StagesDrawerProps) => {
  const { t } = useTranslation();
  const [selectedStage, setSelectedStage] = useState<AdaptedStageOption | null>(null);
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState("");
  const [cipCodes, setCipCodes] = useState<string[]>([]);
  const [dependenciesCodes, setDependenciesCodes] = useState<string[]>([]);

  const isEditing = !!selectedStructurePart;

  const {
    getProgrammingStructuresStages,
    loading,
    programmingStructuresStages,
  } = useGetProgrammingStructuresStages();

  const savedStagesOptions = useMemo(() => {
    const savedStagesById = new Map<string, ProgrammingStructureStage>();
    const savedStagesWithCurrentStageAsDependency = new Map<string, ProgrammingStructureStage>();
    const savedStages = savedStructureParts?.map(part => {
      if (
        selectedStructurePart?.programmingStructureStage?.id !== part.programmingStructureStage.id
      ) {
        savedStagesById.set(part.programmingStructureStage.id, part.programmingStructureStage);
      }

      if (
        !!selectedStructurePart &&
        part?.dependenciesCodes?.includes(selectedStructurePart.programmingStructureStage.code)
      ) {
        savedStagesWithCurrentStageAsDependency.set(
          part.programmingStructureStage.id,
          part.programmingStructureStage,
        );
      }

      return getStagesAsSelectorOption(part.programmingStructureStage);
    });

    return {
      options: savedStages,
      optionsById: savedStagesById,
      optionsWithCurrentStageAsDependency: savedStagesWithCurrentStageAsDependency,
    };
  }, [savedStructureParts, selectedStructurePart]);

  const stagesOptions = useMemo(() => getStagesAsSelectorOptions(programmingStructuresStages), [
    programmingStructuresStages,
    savedStagesOptions,
  ]);

  /**
   * Duration is valid only if is a multiple of 3
   */
  const isDurationValid = useMemo(() => {
    const value = Number(duration);

    if (isNaN(value) || duration === "") return false;

    return value % 3 === 0;
  }, [duration]);

  const isValidForm = isDurationValid && !!selectedStage && !!description;

  useEffect(() => {
    if (isOpen) {
      !loading && getProgrammingStructuresStages(scenario);

      if (selectedStructurePart) {
        const stageOption = getStagesAsSelectorOption(
          selectedStructurePart.programmingStructureStage,
        );
        setSelectedStage(stageOption);
        setDescription(selectedStructurePart.name);
        setDuration(selectedStructurePart.blockDuration.toString());
        setCipCodes(selectedStructurePart.references || []);
        setDependenciesCodes(selectedStructurePart.dependenciesCodes || []);
      } else {
        setSelectedStage(null);
        setDescription("");
        setDuration("");
        setCipCodes([]);
        setDependenciesCodes([]);
      }
    }
  }, [scenario, isOpen]);

  const handleSaveStage = () => {
    if (!isValidForm) return;

    const randomId = Math.random()
      .toString(36)
      .substring(2, 15);

    /**
     * The "new-stage-" prefix is only for frontend usage if there are no id to identify the stage, since the backend will generate an id when the stage is created. This is the case when we are creating a new stage,
     * but also when we are editing an existing stage and we change the selected stage to another one that doesn't have an id (since the id is related to the selected stage).
     * This is necessary to be able to identify the stage in the list of stages when we want to edit it again or when we want to add dependencies with other stages.
     */
    const newStage: ProgrammingStructurePart = {
      id: selectedStructurePart?.id || `new-stage-${randomId}`,
      name: description,
      code: selectedStage?.self.code || "",
      blockDuration: Number(duration),
      references: cipCodes,
      programmingStructureStage: selectedStage?.self || ({} as ProgrammingStructureStage),
      dependenciesCodes,
    };

    onSaveStage?.(newStage);
    onClose?.();
  };

  return (
    <Drawer
      className={styles.stagesDrawer}
      isOpen={isOpen}
      onClose={onClose}
      title={
        <Heading type="h3">
          {t(`programming-structures.form.stages-drawer.title-${isEditing ? "edit" : "create"}`)}
        </Heading>
      }
      closeOnOverlayClick
      footer={
        <footer className={styles.stagesDrawer_actionButtons}>
          <Button variant="ghost" onClick={onClose}>
            {t("generals.cancel")}
          </Button>

          <Button variant="primary" disabled={!isValidForm} onClick={handleSaveStage}>
            {isEditing ? t("generals.save-changes") : t("programming-structures.actions.add-stage")}
          </Button>
        </footer>
      }
    >
      <div className={styles.stagesDrawer_content}>
        <Select
          label={t("programming-structures.form.stages-drawer.stage")}
          placeholder={t("programming-structures.form.stages-drawer.select-stage-placeholder")}
          options={stagesOptions}
          isLoading={loading}
          value={selectedStage}
          isOptionDisabled={(option: any) => savedStagesOptions.optionsById.has(option.value)}
          onChange={option => {
            setSelectedStage(option as AdaptedStageOption);
            setDescription((option as AdaptedStageOption)?.self?.name || "");
          }}
        />

        <TextField
          label={t("programming-structures.form.stages-drawer.description")}
          placeholder={t("programming-structures.form.stages-drawer.description")}
          disabled={!selectedStage}
          value={description}
          onChange={e => setDescription(e.target.value)}
        />

        <TextField
          label={t("programming-structures.form.stages-drawer.duration")}
          placeholder={t("programming-structures.form.stages-drawer.duration-placeholder")}
          value={duration}
          status={!isDurationValid && duration !== "" ? "error" : undefined}
          statusText={t("programming-structures.form.stages-drawer.duration-error")}
          onChange={e => setDuration(e.target.value)}
        />

        <div className={styles.stagesDrawer_codes}>
          <Select
            label={t("programming-structures.form.stages-drawer.dependency")}
            placeholder={t("programming-structures.form.stages-drawer.dependency-placeholder")}
            isOptionDisabled={(option: any) =>
              option?.value === selectedStructurePart?.programmingStructureStage?.id ||
              !!savedStagesOptions?.optionsWithCurrentStageAsDependency?.get(option.value)
            }
            options={savedStagesOptions?.options}
            value={null}
            onChange={option => {
              const value = option ? option?.self?.code : null;

              if (value && !dependenciesCodes.includes(value)) {
                setDependenciesCodes(prev => [...prev, value]);
              }
            }}
          />

          {!!dependenciesCodes?.length && (
            <div className={styles.stagesDrawer_codesList}>
              {dependenciesCodes.map(code => (
                <Tag
                  key={code}
                  className={styles.stagesDrawer_codeTag}
                  icon="close"
                  onIconClick={() => {
                    const newDependenciesCodes = dependenciesCodes?.filter(
                      dependencyCode => dependencyCode !== code,
                    );
                    setDependenciesCodes([...newDependenciesCodes]);
                  }}
                >
                  {code}
                </Tag>
              ))}
            </div>
          )}
        </div>

        <div className={styles.stagesDrawer_codes}>
          <TextField
            key={`cip-codes-${cipCodes?.length}`}
            label={t("programming-structures.form.stages-drawer.cip-codes")}
            placeholder={t("programming-structures.form.stages-drawer.cip-codes-placeholder")}
            onKeyUp={e => {
              if (e.key === "Enter") {
                const value = e.currentTarget.value.trim();
                const isNotSaved = !cipCodes.includes(value);

                if (!!value && isNotSaved) {
                  setCipCodes(prev => [...prev, value]);
                }
              }
            }}
          />

          {!!cipCodes?.length && (
            <div className={styles.stagesDrawer_codesList}>
              {cipCodes.map(code => (
                <Tag
                  key={code}
                  className={styles.stagesDrawer_codeTag}
                  icon="close"
                  onIconClick={() => {
                    const newCipCodes = cipCodes?.filter(savedCode => savedCode !== code);
                    setCipCodes([...newCipCodes]);
                  }}
                >
                  {code}
                </Tag>
              ))}
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default StagesDrawer;
